import { useState, type ReactNode } from 'react';

export type MobileRole = 'primary' | 'secondary' | 'hidden' | 'detail';

export interface Column<T> {
  key: string;
  header: string;
  render: (row: T) => ReactNode;
  mobile?: MobileRole;
  align?: 'left' | 'right' | 'center';
  className?: string;
}

interface Props<T> {
  columns: Column<T>[];
  rows: T[] | undefined;
  rowKey: (row: T) => string | number;
  actions?: (row: T) => ReactNode;
  empty?: string;
  loading?: boolean;
  className?: string;
}

export function ResponsiveTable<T>({
  columns,
  rows,
  rowKey,
  actions,
  empty = 'Sin registros',
  loading,
  className,
}: Props<T>) {
  const rs = rows ?? [];

  return (
    <>
      {/* Desktop table */}
      <div className={`hidden md:block overflow-x-auto ${className ?? ''}`}>
        <table className="table">
          <thead>
            <tr>
              {columns.map((c) => (
                <th key={c.key} className={c.align === 'right' ? 'text-right' : c.align === 'center' ? 'text-center' : ''}>
                  {c.header}
                </th>
              ))}
              {actions && <th className="text-right">Acciones</th>}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={columns.length + (actions ? 1 : 0)} className="text-center text-slate-400 py-6">Cargando…</td></tr>
            )}
            {!loading && rs.length === 0 && (
              <tr><td colSpan={columns.length + (actions ? 1 : 0)} className="text-center text-slate-400 py-6">{empty}</td></tr>
            )}
            {!loading && rs.map((r) => (
              <tr key={rowKey(r)}>
                {columns.map((c) => (
                  <td
                    key={c.key}
                    className={`${c.align === 'right' ? 'text-right' : c.align === 'center' ? 'text-center' : ''} ${c.className ?? ''}`}
                  >
                    {c.render(r)}
                  </td>
                ))}
                {actions && <td className="text-right">{actions(r)}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className={`md:hidden ${className ?? ''}`}>
        {loading && <div className="text-center text-slate-400 py-6 text-sm">Cargando…</div>}
        {!loading && rs.length === 0 && <div className="text-center text-slate-400 py-8 text-sm">{empty}</div>}
        <ul className="divide-y divide-slate-100">
          {!loading && rs.map((r) => (
            <MobileRowCard
              key={rowKey(r)}
              row={r}
              columns={columns}
              actions={actions?.(r)}
            />
          ))}
        </ul>
      </div>
    </>
  );
}

function MobileRowCard<T>({
  row,
  columns,
  actions,
}: {
  row: T;
  columns: Column<T>[];
  actions?: ReactNode;
}) {
  const [open, setOpen] = useState(false);

  const primary = columns.find((c) => c.mobile === 'primary');
  const secondary = columns.find((c) => c.mobile === 'secondary');
  const details = columns.filter(
    (c) => c.mobile !== 'hidden' && c.mobile !== 'primary' && c.mobile !== 'secondary',
  );

  return (
    <li className="py-3 px-1">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-3 text-left"
        aria-expanded={open}
      >
        <div className="min-w-0 flex-1">
          {primary && (
            <div className="text-sm font-semibold text-slate-900 truncate">{primary.render(row)}</div>
          )}
          {!primary && details[0] && (
            <div className="text-sm font-semibold text-slate-900 truncate">{details[0].render(row)}</div>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {secondary && (
            <div className="text-sm font-semibold text-slate-900 text-right">{secondary.render(row)}</div>
          )}
          <svg
            width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            className={`text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`}
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </div>
      </button>
      {open && (
        <div className="mt-3 rounded-md bg-slate-50 border border-slate-100 p-3 space-y-1.5">
          {details.map((c) => (
            <div key={c.key} className="flex justify-between gap-3 text-xs">
              <span className="text-slate-500">{c.header}</span>
              <span className="text-slate-800 text-right break-words max-w-[60%]">{c.render(row)}</span>
            </div>
          ))}
          {actions && (
            <div className="pt-2 mt-2 border-t border-slate-200 flex flex-wrap gap-2 justify-end">
              {actions}
            </div>
          )}
        </div>
      )}
    </li>
  );
}
